/* eslint-disable @typescript-eslint/no-unused-vars */
import { Cell } from 'react-table';
import {
  AdminRole,
  CustomerTierKeys,
  IOrder,
  OrdersStatuses,
  RiderStatus,
} from '../types';
import numeral from 'numeral';
import { VehicleIcon } from '../helpers/riders.helpers';
import React from 'react';
import { maxProcessingMinutes } from '../helpers/general.helpers';
import {
  getOrderCompanyName,
  getOrderCustomerName,
} from '../helpers/orders.helpers';

const paymentMethods = [
  { value: 'TRANSFER', option: 'Transfer' },
  { value: 'WALLET', option: 'Wallet' },
  { value: 'CARD', option: 'Card' },
] as { value: string; option: string }[];

const orderStatus = [
  { value: 'PLACED', label: 'Placed', hideFor: ['vendor'] },
  { value: 'CONFIRMED', label: 'Confirmed', hideFor: ['vendor'] },
  { value: 'PREPARING', label: 'Preparing order', hideFor: ['vendor'] },
  { value: 'PREPARED', label: 'Item prepared', hideFor: [] },
  { value: 'PACKED', label: 'Item packed', hideFor: [] },
  {
    value: 'ACCEPTED',
    label: 'Order assigned to rider',
    hideFor: [],
    riderOnly: true,
  },
  { value: 'READY_FOR_PICKUP', label: 'Ready for Pickup', hideFor: [] },
  { value: 'ENROUTE', label: 'Enroute', hideFor: [], riderOnly: true },
  { value: 'ARRIVED', label: 'Rider arrived', hideFor: [], riderOnly: true },
  { value: 'DELIVERED', label: 'Delivered', hideFor: ['LOGISTICS'] },
  { value: 'CANCELED', label: 'Canceled', hideFor: [] },
] as {
  value: string;
  label: string;
  hideFor: (AdminRole | 'vendor')[];
  riderOnly?: boolean;
}[];

export const riderStatus = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'ENROUTE', label: 'Enroute' },
  { value: 'ARRIVED', label: 'Arrived' },
  { value: 'DELIVERED', label: 'Delivered', hideFor: ['LOGISTICS'] },
] as { value: RiderStatus; label: string; hideFor?: AdminRole[] }[];

const statusColors = {
  PREPARING: 'bg-preparing',
  ENROUTE: 'bg-enroute',
  DELIVERED: 'bg-delivered',
  CANCELED: 'bg-canceled',
  SCHEDULED: 'bg-chow-blue',
  PLACED: 'bg-placed',
  ACCEPTED: 'bg-accepted',
  successful: 'bg-enroute',
  ACTIVE: 'bg-enroute',
  INACTIVE: 'bg-canceled',
  Paid: 'bg-enroute',
  Pending: 'bg-preparing',
  OUT_OF_STOCK: 'bg-preparing',
  Enabled: 'bg-enroute',
  Disabled: 'bg-canceled',
};

const FulfilmentTimer = ({ order }: { order: IOrder }) => {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const isCompleted =
    order.status === 'DELIVERED' || order.status === 'CANCELED';
  if (isCompleted)
    return <span className="text-xs text-text-placeholder">-</span>;

  // Deadline: explicit scheduledTime if provided, else timePaid + max processing window.
  const deadlineMs = order.scheduledTime
    ? new Date(order.scheduledTime).getTime()
    : order.timePaid
      ? new Date(order.timePaid).getTime() +
        maxProcessingMinutes(order) * 60 * 1000
      : null;

  if (!deadlineMs)
    return <span className="text-xs text-text-placeholder">-</span>;

  const remainingMinutes = Math.floor((deadlineMs - now) / (60 * 1000));
  const isPastDeadline = remainingMinutes < 0;
  const absMinutes = Math.abs(remainingMinutes);
  const days = Math.floor(absMinutes / (60 * 24));
  const mins = absMinutes - days * 60 * 24;
  const dayLabel = days === 1 ? 'day' : 'days';
  const body = `${isPastDeadline ? '-' : ''}${days} ${dayLabel} : ${mins} mins`;
  const label = isPastDeadline ? body : `${body} left`;

  // SLA: order turns red once 48 hours have elapsed from order start without fulfilment.
  const startMs = order.timePaid
    ? new Date(order.timePaid).getTime()
    : order.timePlaced
      ? new Date(order.timePlaced).getTime()
      : null;
  const SLA_MINUTES = 48 * 60;
  const isSlaBreached = startMs
    ? (now - startMs) / (60 * 1000) > SLA_MINUTES
    : false;

  return (
    <span
      className={`text-xs medium px-2 py-1 rounded-md inline-flex items-center whitespace-nowrap ${isSlaBreached ? 'bg-red-50 text-red-500' : ''}`}
    >
      {label}
    </span>
  );
};

const orderGroups = [
  { name: 'All orders', value: '' },
  { name: 'New orders', value: 'CONFIRMED' },
  { name: 'Processing', value: 'PREPARING' },
  { name: 'Ready for pickup', value: 'READY_FOR_PICKUP' },
  { name: 'In transit', value: 'ENROUTE' },
  { name: 'Delivered', value: 'DELIVERED' },
  { name: 'Cancelled', value: 'CANCELED' },
  { name: 'Abandoned checkout', value: 'ABANDONED' },
];

const paymentStatus = [
  { name: 'Pending', value: 0 },
  { name: 'Confirmed', value: 1 },
];

const orderColumns = (type: 'abandoned' | 'paid' = 'paid') => [
  {
    Header: 'Order ID',
    accessor: 'slug',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      return (
        <span className="text-sm medium">
          #{order.slug?.replace('ORD-', '') || order.id}
        </span>
      );
    },
  },
  {
    Header: 'Customer',
    accessor: 'customer',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      const companyName = getOrderCompanyName(order);
      const customerName = getOrderCustomerName(order);
      return (
        <div className="flex flex-col">
          <span className="text-sm">{customerName}</span>
          {companyName && (
            <span className="text-[10px] medium text-primary uppercase tracking-wide">
              Company
            </span>
          )}
        </div>
      );
    },
  },
  {
    Header: 'Fulfilment time',
    accessor: 'timePreparing',
    Cell: ({ row }: Cell) => <FulfilmentTimer order={row.original as IOrder} />,
  },
  {
    Header: 'Item',
    accessor: 'orderItems',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      const items = order.orderItems || order.meals;
      if (!items?.length) return '-';
      const name =
        (items[0] as any).foodItem?.name || (items[0] as any).name || '-';
      return <span className="truncate max-w-[180px] block">{name}</span>;
    },
  },
  {
    Header: 'Vendor',
    accessor: 'brands',
    Cell: ({ row }: Cell) => row.values.brands?.[0]?.name || '-',
  },
  {
    Header: 'Outlet',
    accessor: 'kitchens',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      if (order.slug?.startsWith('EXT_') && order.metadata?.tenantName) {
        return order.metadata.tenantName;
      }
      return row.values.kitchens?.[0]?.name || '-';
    },
  },
  {
    Header: 'Amount',
    accessor: 'totalPrice',
    Cell: ({ row }: Cell) => `₦${numeral(row.values.totalPrice).format('0,0')}`,
  },
  {
    Header: 'Delivery method',
    accessor: 'OrderService',
    Cell: ({ row }: Cell) => {
      const services = (row.original as IOrder).OrderService;
      if (!services?.length) return 'Delivery';
      return services
        .map((s: any) => s.service || s.type || 'Delivery')
        .join(', ');
    },
  },
  {
    Header: 'Status',
    accessor: 'status',
  },
  {
    Header: 'Exp. delivery date/time',
    accessor: 'scheduledTime',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      return order.scheduledTime
        ? new Date(order.scheduledTime).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : order.timePaid
          ? new Date(order.timePaid).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '-';
    },
  },
  ...(type === 'paid'
    ? [
        {
          Header: 'Date ordered',
          accessor: 'timePaid',
        },
      ]
    : [
        {
          Header: 'Date ordered',
          accessor: 'timePlaced',
        },
      ]),

  {
    Header: 'Actions',
    accessor: 'id',
  },
];

const orderLabels = [
  { value: 'OFFICE', option: 'Office' },
  { value: 'HOME', option: 'Home' },
  { value: 'OTHER', option: 'Other' },
  { value: 'WORK', option: 'Work' },
] as { value: string; option: string }[];

const orderViews = [{ name: 'List' }, { name: 'Map' }];

const orderQuantity = [
  { name: '1', value: 1 },
  { name: '2', value: 2 },
  { name: '3', value: 3 },
  { name: '4', value: 4 },
  { name: '5', value: 5 },
  { name: '6', value: 6 },
  { name: '7', value: 7 },
];

export const returnDeliveryInformation = (order: IOrder) => {
  const isExternal = order.slug?.startsWith('EXT_');

  const baseFields = [
    {
      title: isExternal ? 'Pickup Store' : 'Kitchen Assigned',
      value: isExternal
        ? order.metadata?.pickupStoreName
        : order?.kitchens.map((kitchen) => kitchen.name).join(', '),
      changeButton: !isExternal,
    },
    {
      title: 'Delivery Rider Assigned',
      value:
        order?.pilot &&
        order.pilot.user &&
        `${order.pilot.user.firstName} ${order.pilot.user.lastName}`,
      changeButton: true,
    },
    {
      title: "Delivery Rider's phone number",
      value:
        order?.pilot && order.pilot.user && `${order.pilot.user.phoneNumber}`,
      changeButton: false,
    },
    {
      title: 'Order Channel',
      value: order?.channel && order.channel.channelName,
      changeButton: false,
    },
    {
      title: 'Assigned kitchen staffs',
      value: order.assignedKitchenStaffs
        ?.map(
          (staff) =>
            `${staff.user.firstName} ${staff.user.lastName}: ${staff.role.join(
              ', ',
            )}`,
        )
        .join(', '),
      changeButton: false,
    },
  ];

  const externalFields = isExternal
    ? [
        {
          title: 'Tenant Name',
          value: order.metadata?.tenantName,
          changeButton: false,
        },
        {
          title: 'Pickup Contact Person',
          value: order.metadata?.pickupContactPerson,
          changeButton: false,
        },
        {
          title: 'Pickup Phone Number',
          value: order.metadata?.pickupPhoneNumber,
          changeButton: false,
        },
        {
          title: 'Pickup Email',
          value: order.metadata?.pickupEmail,
          changeButton: false,
        },
        {
          title: 'Customer Phone (from tenant)',
          value: order.metadata?.customerPhoneNumber,
          changeButton: false,
        },
      ]
    : [];

  return [...baseFields, ...externalFields];
};

export const initialOrdersStatuses: OrdersStatuses = {
  PLACED: 0,
  PREPARING: 0,
  READY_FOR_PICKUP: 0,
  CONFIRMED: 0,
  ACCEPTED: 0,
  ENROUTE: 0,
  DELIVERED: 0,
  CANCELED: 0,
  SCHEDULED: 0,
};

export const flaggedOrderColumns = [
  {
    Header: 'Order Slug',
    accessor: 'order',
    Cell: ({ row }: Cell) => row.values.order.slug,
  },
  {
    Header: 'Date & Time',
    accessor: 'createdAt',
  },
  {
    Header: 'Pilot',
    accessor: 'pilot',
    Cell: ({ row }: Cell) => (
      <div className="flex items-center space-x-2">
        <p>{row.values.pilot ? row.values.pilot.user.firstName : '-'}</p>
      </div>
    ),
  },
  {
    Header: 'Flag',
    accessor: 'flag',
    Cell: ({ row }: Cell) => row.values.flag.value,
  },
  // {
  //   Header: 'Customer',
  //   accessor: 'customer',
  //   Cell: ({ row }: Cell) =>
  //     row.values.customer
  //       ? row.values.customer.fullName
  //       : row.values.user
  //       ? `${row.values.user.firstName} ${row.values.user.lastName}`
  //       : '-',
  // },
  // {
  //   Header: 'Delivery Address',
  //   accessor: 'deliveryAddress',
  //   Cell: ({ row }: Cell) => row.values.deliveryAddress.address,
  // },
  {
    Header: 'Status',
    accessor: 'status',
  },
];

export {
  paymentMethods,
  orderStatus,
  statusColors,
  orderGroups,
  paymentStatus,
  orderColumns,
  orderLabels,
  orderViews,
  orderQuantity,
  FulfilmentTimer,
};
